"use client";

import { useEffect, useRef, useState } from "react";
import { Database, Funnel as Filter, ChartLine as LineChart, Layers, Send } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "DISCOVER",
  description: "We begin by mapping your operational landscape, identifying core requirements and technical constraints through deep-dive discovery sessions.",
  icon: Database
}, {
  number: "02",
  title: "STRATEGIZE",
  description: "Our architects synthesize findings into a robust roadmap, prioritizing high-impact milestones that align with your long-term business objectives.",
  icon: Filter
}, {
  number: "03",
  title: "ENGINEER",
  description: "Precision development begins, utilizing industry-leading frameworks to build scalable, secure, and high-performance digital infrastructure.",
  icon: LineChart
}, {
  number: "04",
  title: "INTEGRATE",
  description: "Seamless deployment into your existing ecosystem, ensuring full compatibility and optimized performance across all internal systems.",
  icon: Layers
}, {
  number: "05",
  title: "OPTIMIZE",
  description: "Continuous monitoring and iterative refinement ensure your solution evolves alongside your business, maintaining peak operational efficiency.",
  icon: Send
}];
export default function ProcessScoping() {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: quoteRef,
    isVisible: quoteVisible
  } = useRevealOnScroll<HTMLDivElement>();
  useEffect(() => {
    const handleScroll = () => {
      const node = sectionRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalDistance = rect.height + windowHeight;
      const scrolled = windowHeight - rect.top;
      const ratio = Math.max(0, Math.min(1, scrolled / totalDistance));
      const adjusted = Math.max(0, Math.min(1, (ratio - 0.15) / 0.65));
      setProgress(adjusted);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);
  return <section data-section-id="3370" id="process-scoping" ref={sectionRef} className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-44 px-6 md:px-10 lg:px-16">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-multiply" style={{
      backgroundImage: "radial-gradient(var(--accent) 1px, transparent 1px)",
      backgroundSize: "3px 3px"
    }} aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`text-center max-w-4xl mx-auto mb-20 md:mb-28 lg:mb-36 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="font-mono text-xs md:text-sm tracking-[0.2em] mb-8 text-primary">
            // 02 — OUR PROCESS
          </div>
          <h2 className="font-default font-light leading-[1.05] tracking-tight text-4xl md:text-6xl lg:text-[80px] text-foreground">
            Turning complex challenges into{" "}
            <em className="italic font-normal text-primary">
              elegant solutions.
            </em>
          </h2>
          <p className="font-default mt-8 text-base md:text-lg leading-relaxed max-w-2xl mx-auto text-card-foreground">
            Our structured five-step methodology ensures transparency, precision, and measurable results at every stage of your digital transformation.
          </p>
        </div>

        {}
        <div className="hidden lg:block relative">
          {}
          <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-border" aria-hidden="true" />
          {}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 h-px transition-[width] duration-300 ease-out bg-primary shadow-lg" style={{
          width: `${progress * 100}%`
        }} aria-hidden="true" />

          <div className="grid grid-cols-5 relative min-h-[560px]">
            {steps.map((step, i) => {
            const Icon = step.icon;
            const isAbove = i % 2 === 0;
            const nodeProgress = (i + 0.5) / steps.length;
            const isActive = progress >= nodeProgress - 0.05;
            return <div key={step.number} data-index={i} className="relative flex flex-col items-center">
                  {}
                  {isAbove && <div className="absolute bottom-1/2 mb-12 w-full px-4 pb-2 text-center">
                      <Icon className={`mx-auto mb-4 size-6 ${isActive ? "text-primary" : "text-card-foreground"}`} strokeWidth={1.25} />
                      <div className="font-mono text-xs tracking-[0.2em] mb-3 text-primary">
                        {step.number}
                      </div>
                      <h3 className="font-default font-normal text-3xl xl:text-4xl mb-4 leading-tight text-foreground">
                        {step.title}
                      </h3>
                      <p className="font-default text-sm leading-relaxed max-w-[240px] mx-auto text-card-foreground">
                        {step.description}
                      </p>
                    </div>}

                  {}
                  <div className="absolute top-1/2 -translate-y-1/2 flex items-center justify-center">
                    <div className="relative rounded-lg flex items-center justify-center bg-foreground" style={{
                  width: "20px",
                  height: "20px"
                }}>
                      <div className={`rounded-lg transition-all duration-500 ${isActive ? "bg-primary shadow-lg" : "bg-background"}`} style={{
                    width: "8px",
                    height: "8px"
                  }} />
                      {isActive && <span className="absolute inline-flex h-full w-full rounded-lg opacity-40 animate-ping bg-primary" aria-hidden="true" />}
                    </div>
                  </div>

                  {}
                  {!isAbove && <div className="absolute top-1/2 mt-12 w-full px-4 pt-2 text-center">
                      <div className="font-mono text-xs tracking-[0.2em] mb-3 text-primary">
                        {step.number}
                      </div>
                      <h3 className="font-default font-normal text-3xl xl:text-4xl mb-4 leading-tight text-foreground">
                        {step.title}
                      </h3>
                      <p className="font-default text-sm leading-relaxed max-w-[240px] mx-auto mb-4 text-card-foreground">
                        {step.description}
                      </p>
                      <Icon className={`mx-auto size-6 ${isActive ? "text-primary" : "text-card-foreground"}`} strokeWidth={1.25} />
                    </div>}
                </div>;
          })}
          </div>
        </div>

        {}
        <div className="lg:hidden relative">
          {}
          <div className="absolute left-[19px] top-0 bottom-0 w-px bg-border" aria-hidden="true" />
          {}
          <div className="absolute left-[19px] top-0 w-px transition-[height] duration-300 ease-out bg-primary shadow-lg" style={{
          height: `${progress * 100}%`
        }} aria-hidden="true" />

          <div className="space-y-12">
            {steps.map((step, i) => {
            const Icon = step.icon;
            const nodeProgress = (i + 0.5) / steps.length;
            const isActive = progress >= nodeProgress - 0.05;
            return <div key={step.number} data-index={i} className="relative pl-14">
                  {}
                  <div className="absolute left-[10px] top-1 flex items-center justify-center">
                    <div className="relative flex items-center justify-center bg-foreground rounded-lg" style={{
                  width: "20px",
                  height: "20px"
                }}>
                      <div className={`transition-all duration-500 rounded-lg ${isActive ? "bg-primary shadow-lg" : "bg-background"}`} style={{
                    width: "8px",
                    height: "8px"
                  }} />
                    </div>
                  </div>

                  <div className="flex items-start gap-3 mb-3">
                    <Icon className={`size-5 mt-1 ${isActive ? "text-primary" : "text-card-foreground"}`} strokeWidth={1.25} />
                    <div className="font-mono text-xs tracking-[0.2em] pt-1.5 text-primary">
                      {step.number}
                    </div>
                  </div>
                  <h3 className="font-default font-normal text-3xl mb-3 leading-tight text-foreground">
                    {step.title}
                  </h3>
                  <p className="font-default text-sm leading-relaxed text-card-foreground">
                    {step.description}
                  </p>
                </div>;
          })}
          </div>
        </div>

        {}
        <div ref={quoteRef} className={`mt-24 md:mt-32 lg:mt-44 max-w-3xl mx-auto text-center transition-all duration-1000 ease-out ${quoteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="mx-auto mb-8 h-px w-16 bg-primary" aria-hidden="true" />
          <blockquote className="font-default italic font-light text-2xl md:text-3xl lg:text-[36px] leading-snug text-foreground">
            "Excellence is not an act, but a habit of precision and intent."
          </blockquote>
          <div className="font-mono text-xs tracking-[0.2em] mt-6 text-card-foreground">
            — LEAD ARCHITECT, EVERMARK
          </div>
        </div>
      </div>
    </section>;
}